import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Divider,
  Drawer,
  Fade,
  Grid,
  Typography,
} from '@mui/material';
import useRouter, { type RouteLocationOptions } from '~/hooks/useRouter';
import { deserializeAxiosError } from '~/lib/axios';
import type { Searches } from '~/types';

import { isAxiosError } from 'axios';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router';
import { Button } from '~/components/Button';
import { PageTemplate } from '~/components/PageTemplate';
import { RecaptchaDialog } from '~/components/RecaptchaDialog';
import SearchLoadingDialog from '~/components/SearchLoadingDialog';
import { SearchResultsTable } from '~/components/SearchResultsTable';
import LoadingResults from '~/components/SearchResultsTable/LoadingResults';
import NoResultsFound from '~/components/SearchResultsTable/NoResultsFound';
import useCookieStorage from '~/hooks/useCookieStorage';
import useDownloadFile from '~/hooks/useDownloadFile';
import useFeature from '~/hooks/useFeature';
import useFiltersFromQueryParam from '~/hooks/useFiltersFromQueryParam';
import useIsNavigating from '~/hooks/useIsNavigating';
import useLocalStorage from '~/hooks/useLocalStorage';
import { useNameFromSearch } from '~/hooks/useNameFromSearch';
import useReportPagination from '~/hooks/useReportPagination';
import useRoute from '~/hooks/useRoute';
import { usePutSearch } from '~/hooks/useSearchReportfromSearchObject';
import { useTrackSearch } from '~/hooks/useTrackSearch';
import { useUser } from '~/hooks/useUser';
import DidYouMean from '~/scenes/reports/DidYouMean';
import { DownloadDrawer } from '~/scenes/reports/DownloadDrawer';
import DownloadLoadingModal from '~/scenes/reports/DownloadLoadingModal';
import DownloadSuccess from '~/scenes/reports/DownloadSuccess';
import ForcedSignupDialog from '~/scenes/reports/ForcedSignupDialog';
import ReportPageHeader from '~/scenes/reports/ReportPageHeader';
import RecentSearchesList from '~/scenes/reports/RecentSearchesList';
import RecommendedSearches from '~/scenes/reports/RecommendedSearches';
import ReportEditor from '~/scenes/reports/ReportEditor';
import RightColumnHeader from '~/scenes/reports/RightColumnHeader';
import useSortFromQueryParam, {
  defaultSortString,
} from '~/scenes/reports/useSortFromQueryParam';
import {
  REPORT_EDITOR_WIDTH_STORAGE_KEY,
  SHOW_SNIPPETS_PREFERENCE_COOKIE_NAME,
} from '~/scripts/constants';
import { useHandleException } from '~/scripts/handleException';
import { isEmptySearch } from '~/scripts/isEmptySearch';
import { is401Status, is402Status } from '~/scripts/network';
import { getRowLimitFromPlanId } from '~/scripts/planLimits';
import { useRecaptcha } from '~/scripts/recaptcha';
import { addGuestSearchHistory } from '~/scripts/searchHistory';
import type { Filters } from '~/types/filters';
import type { Route } from './+types/reports_.$name.($uuid)';
import { useQuery } from '~/hooks/useQuery';
import api from '~/api/Endpoints';
import { useScreenSize } from '~/hooks/useScreenSize';

export { clientLoader, loader } from '~/scenes/reports/loader';
export { meta } from '~/scenes/reports/meta';

export default function ReportPage({
  loaderData,
  params,
}: Route.ComponentProps) {
  const router = useRouter();
  const route = useRoute();
  const isNavigating = useIsNavigating();
  const recaptcha = useRecaptcha();
  const handleException = useHandleException();
  const trackSearch = useTrackSearch();
  const [currentSearch, setCurrentSearch] =
    React.useState<Searches.SearchObject | null>(loaderData.search || null);
  // True only when currentSearch was changed by the user via onUpdateClick,
  // not when it was reset by the loader sync effect.
  const userEditedSearchRef = useRef(false);

  const { data: allFacets } = useQuery({
    queryKey: ['facets'],
    queryFn: async () => {
      const response = await api.get('/app/search/facets');
      return response.facets;
    },
  });
  const location = useLocation();
  const isNewReportPage = useMemo(() => {
    return !loaderData.uuid;
  }, [loaderData.uuid]);

  // global totals for an empty search, otherwise the (unfiltered) search's own facets
  const facets = useMemo(() => {
    const searchFacets = loaderData.searchFacets;
    if (
      !isEmptySearch(loaderData.search) &&
      searchFacets &&
      Object.keys(searchFacets).length
    ) {
      return searchFacets;
    }
    return allFacets;
  }, [loaderData.search, loaderData.searchFacets, allFacets]);

  useEffect(() => {
    userEditedSearchRef.current = false;
    setCurrentSearch(loaderData.search || { all: [], any: [], none: [] });
    setTemporarySearch(loaderData.search || { all: [], any: [], none: [] });
    lastUrlRef.current = {
      path: route.path,
      query: route.query,
    };
  }, [loaderData.search, route.path, route.query]);

  const user = useUser();

  useEffect(() => {
    if (user.isGuest && loaderData.uuid) {
      addGuestSearchHistory({
        uuid: loaderData.uuid,
        timestamp: new Date(),
      });
    }
  }, [loaderData.uuid, user.isGuest]);

  const [temporarySearch, setTemporarySearch] =
    React.useState<Searches.SearchObject>(
      loaderData.search || { all: [], any: [], none: [] }
    );

  const filters = useFiltersFromQueryParam(route.query.filters);

  const [newFilters, setNewFilters] = React.useState<Filters.Filters>(
    JSON.parse(JSON.stringify(filters))
  );

  useEffect(() => {
    setNewFilters(JSON.parse(JSON.stringify(filters)));
  }, [filters]);

  const [editorWidthStorage, setEditorWidthStorage] = useLocalStorage(
    REPORT_EDITOR_WIDTH_STORAGE_KEY,
    '400'
  );
  const [editorWidth, setEditorWidth] = useState<number>(
    Number(editorWidthStorage) || 400
  );
  useEffect(() => {
    if (editorWidthStorage) {
      setEditorWidth(Number(editorWidthStorage) || 400);
    }
  }, [editorWidthStorage]);

  const isResizingRef = useRef(false);

  const handleResizeStart = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      isResizingRef.current = true;

      const onMouseMove = (moveEvent: MouseEvent) => {
        if (!isResizingRef.current) {
          return;
        }
        const newWidth = Math.min(Math.max(moveEvent.clientX, 328), 750);
        setEditorWidth(newWidth);
        if (moveEvent.clientX < 328) {
          setIsEditingSearch(false);
        }
      };

      const onMouseUp = (upEvent: MouseEvent) => {
        isResizingRef.current = false;
        const finalWidth = Math.min(Math.max(upEvent.clientX, 328), 750);
        setEditorWidthStorage(String(finalWidth));
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [setEditorWidthStorage]
  );

  const [downloadDrawerOpen, setDownloadDrawerOpen] = React.useState(false);
  const [showSnippets, setShowSnippets] = useCookieStorage(
    SHOW_SNIPPETS_PREFERENCE_COOKIE_NAME,
    '1'
  );

  const [, sortField, sortOrder] = useSortFromQueryParam(
    route.query.sort as string | undefined
  );

  const { pageNumber, handlePageChange } = useReportPagination(
    route.query.start as string | undefined
  );

  const isFiltersDirty = useMemo(
    () => JSON.stringify(filters) !== JSON.stringify(newFilters),
    [filters, newFilters]
  );

  const isSearchDirty = useMemo(
    () => JSON.stringify(currentSearch) !== JSON.stringify(temporarySearch),
    [currentSearch, temporarySearch]
  );

  const name = useNameFromSearch(loaderData?.search);

  const { data: updatedSearchForm, isLoading: isLoadingUpdatedSearchForm } =
    usePutSearch(currentSearch);

  const [recaptchaToken, setRecaptchaToken] = useState<string>('');
  const [forcedSignupDialogOpen, setForcedSignupDialogOpen] =
    React.useState(false);

  const [isDownloading, setIsDownloading] = useState(false);

  const [downloadRows, setDownloadRows] = useState<number>();
  const [download_recaptcha_feature_on] = useFeature('download_recaptcha');
  const [downloadColumns, setDownloadColumns] = useState<
    Record<string, string>
  >({});
  const rowLimit = useMemo(
    () => getRowLimitFromPlanId(user.account.plans_id),
    [user.account.plans_id]
  );

  const requiresDownloadRecaptcha = user.isGuest
    ? false
    : download_recaptcha_feature_on;
  const skipRecaptcha = route.query.recaptcha === '1';
  const [recaptchaOpen, setRecaptchaOpen] = useState(false);

  const lastUrlRef = useRef<RouteLocationOptions | null>(null);

  const revertSearchToLastUrl = useCallback(() => {
    if (lastUrlRef.current) {
      router.replace(lastUrlRef.current);
    }
  }, [router, lastUrlRef]);

  const abortSearch = useCallback(() => {
    window.abortController?.abort('abortSearch');
    window.abortController = new AbortController();
  }, []);

  useEffect(() => {
    window.abortController = new AbortController();
    const onSearchAbort = () => {
      abortSearch();
    };
    window.addEventListener('abortSearch', onSearchAbort);
    return () => {
      window.removeEventListener('abortSearch', onSearchAbort);
    };
  }, [abortSearch]);

  useEffect(() => {
    if (!isDownloading) {
      setRecaptchaOpen(false);
      setRecaptchaToken('');
      setDownloadRows(undefined);
      setDownloadColumns({});
    }
  }, [isDownloading]);

  useEffect(() => {
    if (downloadDrawerOpen) {
      setIsDownloading(false);
      setRecaptchaOpen(false);
      setRecaptchaToken('');
    }
  }, [downloadDrawerOpen]);

  const download = useDownloadFile({
    search: loaderData?.search,
    columns: downloadColumns,
    recaptchaToken: recaptchaToken && skipRecaptcha ? '1' : recaptchaToken,
    requiresDownloadRecaptcha,
    total: loaderData?.total || 0,
    uuid: loaderData?.uuid,
    isDownloading,
    setIsDownloading,
    setRecaptchaOpen,
    rows: downloadRows,
    filters: filters,
  });

  useEffect(() => {
    recaptcha('report_page');
    trackSearch({
      url: `${route.path}?${new URLSearchParams(route.query as Record<string, string>).toString()}`,
      user,
      total: loaderData?.total || 0,
      name,
      uuid: loaderData?.uuid || '',
    });
  }, [
    route.path,
    route.query,
    recaptcha,
    user,
    name,
    loaderData?.total,
    loaderData?.uuid,
    trackSearch,
  ]);

  const redirectToNewReport = useCallback(() => {
    router.push({
      path: '/reports/new',
      query: {
        ...route.query,
        search: undefined,
        sort: undefined,
        filters: undefined,
      },
    });
  }, [route.query, router]);

  const isLoadingResults = useMemo(() => {
    return (
      isLoadingUpdatedSearchForm ||
      isNavigating ||
      !!loaderData?.isLoading ||
      !!loaderData?.retryAfter
    );
  }, [
    isLoadingUpdatedSearchForm,
    isNavigating,
    loaderData?.isLoading,
    loaderData?.retryAfter,
  ]);

  useEffect(() => {
    // Don't redirect while the put search request is in flight — updatedSearchForm
    // may still be from the previous report and would cause a redirect loop when
    // switching reports.
    if (isLoadingUpdatedSearchForm) {
      return;
    }
    // default null form
    if (isEmptySearch(currentSearch)) {
      return;
    }

    // currentSearch is updated via a separate useEffect (which runs after render),
    // so there's a window where route.path has updated to the new report but
    // currentSearch still holds the previous report's search. If they're out of sync
    // AND the user hasn't explicitly edited the search, bail — updatedSearchForm still
    // reflects the old report and would redirect back to it.
    if (
      !userEditedSearchRef.current &&
      loaderData.search &&
      JSON.stringify(currentSearch) !== JSON.stringify(loaderData.search)
    ) {
      return;
    }

    if (route.path === '/reports/new') {
      return;
    }

    if (isNavigating) {
      return;
    }

    if (isEmptySearch(currentSearch) && route.path !== '/reports/new') {
      redirectToNewReport();
      return;
    }

    if (
      userEditedSearchRef.current &&
      updatedSearchForm &&
      updatedSearchForm.url !== route.path
    ) {
      const path = updatedSearchForm.url;
      if (path !== location.pathname) {
        router.push({
          path: path,
          query: {
            ...route.query,
            search: undefined,
            retryAfter: loaderData?.retryAfter ? '1' : undefined,
            start: undefined,
          },
        });
      }
    }

    if (route.query.retryAfter && !loaderData?.retryAfter) {
      redirectToNewReport();
    }
  }, [
    updatedSearchForm,
    isLoadingUpdatedSearchForm,
    redirectToNewReport,
    location.pathname,
    isNavigating,
    router,
    route,
    loaderData?.retryAfter,
    loaderData.search,
    currentSearch,
  ]);

  const onUpdateClick = useCallback(
    (search: Searches.SearchObject | null, updatedFilters: Filters.Filters) => {
      const filtersParam =
        Object.keys(updatedFilters || {}).length === 0
          ? undefined
          : JSON.parse(JSON.stringify(updatedFilters));

      const filtersDirty =
        JSON.stringify(filters) !== JSON.stringify(updatedFilters);

      if (isSearchDirty) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        if (isNewReportPage) {
          return router.push({
            path: '/reports/new',
            query: {
              ...route.query,
              search: JSON.stringify(search),
              filters: filtersParam,
            },
          });
        } else {
          userEditedSearchRef.current = true;
          setCurrentSearch(search);
        }
      }

      if (filtersDirty && user.isGuest) {
        return router.push({
          path: '/signup',
          query: {
            ...route.query,
            reason: 'search.filters',
          },
        });
      }
      if (filtersDirty) {
        router.push({
          path: route.path,
          query: {
            ...route.query,
            filters: filtersParam,
          },
        });
      }
    },
    [
      isSearchDirty,
      isNewReportPage,
      router,
      route,
      setCurrentSearch,
      filters,
      user.isGuest,
    ]
  );

  const onDiscardChangesClick = useCallback(() => {
    setCurrentSearch(loaderData.search || { all: [], any: [], none: [] });
    setTemporarySearch(loaderData.search || { all: [], any: [], none: [] });
    setNewFilters(JSON.parse(JSON.stringify(filters)));
  }, [loaderData.search, filters]);

  const onDownloadClick = () => {
    if (user.isGuest) {
      return router.push({
        path: '/signup',
        query: {
          search: params.uuid,
          reason: 'search.download',
        },
      });
    }

    setDownloadDrawerOpen(true);
  };

  const showRightColumnHeader = useMemo(() => {
    if (isNewReportPage) {
      return false;
    }
    if (loaderData.total === 0) {
      return false;
    }
    if (Object.keys(filters).length > 0) {
      return true;
    }
    return true;
  }, [filters, isNewReportPage, loaderData.total]);

  const handleFiltersChange = useCallback(
    (updates: Filters.Filters) => {
      setNewFilters(updates);
    },
    [setNewFilters]
  );

  useEffect(() => {
    const error = deserializeAxiosError(loaderData?.error);
    if (!error) {
      return;
    }

    if (isAxiosError(error)) {
      if (error.code === 'ERR_CANCELED' || error?.name === 'AbortError') {
        return;
      }
      if (error?.message === 'CACHE_MISS') {
        return;
      }
      if (is401Status(error)) {
        setForcedSignupDialogOpen(true);
        return;
      }
      if (is402Status(error)) {
        router.push({
          path: '/pricing',
          query: {
            reason: 'search',
          },
        });
        return;
      }
    }
    handleException(error, {
      category: 'search',
    });
  }, [loaderData?.error, lastUrlRef, router, handleException]);

  const { lgAndUp } = useScreenSize();

  const [isEditingSearch, setIsEditingSearch] = useState(lgAndUp);
  useEffect(() => {
    if (lgAndUp) {
      setIsEditingSearch(true);
    }
  }, [lgAndUp]);

  const RenderEditor = useMemo(() => {
    return (
      <ReportEditor
        abortSearch={abortSearch}
        revertSearchToLastUrl={revertSearchToLastUrl}
        onUpdateClick={() => onUpdateClick(temporarySearch, newFilters)}
        temporarySearch={temporarySearch}
        setTemporarySearch={setTemporarySearch}
        onDiscardChangesClick={onDiscardChangesClick}
        newFilters={newFilters}
        facets={facets}
        name={name}
        isLoading={isLoadingResults}
        isSearchDirty={isSearchDirty}
        isFiltersDirty={isFiltersDirty}
        handleFiltersChange={handleFiltersChange}
        onClose={() => setIsEditingSearch(false)}
      />
    );
  }, [
    temporarySearch,
    newFilters,
    onDiscardChangesClick,
    name,
    isLoadingResults,
    isSearchDirty,
    isFiltersDirty,
    handleFiltersChange,
    onUpdateClick,
    facets,
    revertSearchToLastUrl,
    abortSearch,
  ]);

  return (
    <PageTemplate fluid breadcrumbs={loaderData?.breadcrumbs || []}>
      <Grid container spacing={0}>
        {
          <Grid item xs={12}>
            <ReportPageHeader
              loading={isLoadingResults}
              name={name}
              search={loaderData?.search}
              temporarySearch={temporarySearch}
              report={loaderData?.report}
              uuid={loaderData?.uuid}
              reportDetails={loaderData?.reportDetails}
              logo={loaderData?.logo || 'logo'}
              isDirty={isFiltersDirty || isSearchDirty}
              onDiscardChangesClick={onDiscardChangesClick}
              onUpdateClick={() => onUpdateClick(temporarySearch, newFilters)}
              total={loaderData?.total || 0}
              setIsEditingSearch={setIsEditingSearch}
              error={!!loaderData?.error}
              partial_results={loaderData?.partial_results}
              history={loaderData?.history}
              actions={[
                <Button
                  key="download"
                  variant="contained"
                  color="primary"
                  disabled={loaderData?.total === 0 || isLoadingResults}
                  loading={isDownloading && !download.file}
                  sx={{ px: 4 }}
                  onClick={() => onDownloadClick()}
                  startIcon={<DownloadIcon />}>
                  Download
                </Button>,
              ]}
            />
          </Grid>
        }
        {
          <>
            <Grid
              container
              className="right-column"
              item
              xs={true}
              sx={{
                transition: 'all 0.2s ease-in-out',
                alignItems: 'start',
                alignContent: 'start',
                height: '100vh',
              }}>
              {loaderData?.search && (
                <Grid item xs={12}>
                  <DidYouMean search={loaderData?.search} />
                </Grid>
              )}

              {showRightColumnHeader && (
                <RightColumnHeader
                  isEditingSearch={isEditingSearch}
                  setIsEditingSearch={setIsEditingSearch}
                  filters={newFilters}
                  uuid={loaderData?.uuid || ''}
                  onFiltersRemove={(updates) => {
                    setNewFilters(updates);
                  }}
                  showSnippets={showSnippets}
                  setShowSnippets={setShowSnippets}
                  total={loaderData.total || 0}
                  history={loaderData.history}
                  isLoadingResults={isLoadingResults}
                  search={loaderData?.search}
                  name={name}
                />
              )}

              <Grid container item xs={12} className="main-search-container">
                <Fade
                  in={(lgAndUp && isEditingSearch) || isNewReportPage}
                  mountOnEnter
                  unmountOnExit>
                  <Grid
                    container
                    item
                    className="left-column"
                    sx={{
                      transition: isResizingRef.current
                        ? 'none'
                        : 'width 0.2s ease-in-out',
                      width:
                        lgAndUp && !isNewReportPage
                          ? `${editorWidth}px`
                          : { xs: '100%', lg: isNewReportPage ? '66%' : '33%' },
                      flexBasis:
                        lgAndUp && !isNewReportPage ? 'auto' : undefined,
                      maxWidth:
                        lgAndUp && !isNewReportPage ? 'none' : undefined,
                    }}
                    alignContent={'start'}>
                    {RenderEditor}
                  </Grid>
                </Fade>
                {isEditingSearch && lgAndUp && !isNewReportPage && (
                  <Divider
                    orientation="vertical"
                    data-fullstory="reports-editor-resize"
                    flexItem
                    onMouseDown={handleResizeStart}
                    sx={{
                      marginLeft: '-1px',
                      cursor: 'col-resize',
                      width: '5px',
                      '&:hover': {
                        borderColor: 'primary.main',
                      },
                    }}
                  />
                )}
                {isEditingSearch && lgAndUp && isNewReportPage && (
                  <Divider
                    orientation="vertical"
                    flexItem
                    sx={{ marginLeft: '-1px' }}
                  />
                )}
                {isNewReportPage && !isLoadingResults && (
                  <>
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ marginLeft: '-1px' }}
                    />
                    <Grid container item xs={12} lg={4} xl={5}>
                      <RecentSearchesList />
                      <Box>
                        <Typography
                          variant="h6"
                          fontWeight={'bold'}
                          sx={{ p: 1.9, pl: 4 }}>
                          ✨ Popular Examples
                        </Typography>
                      </Box>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>

                      <Grid item xs={12}>
                        <RecommendedSearches />
                      </Grid>
                    </Grid>
                  </>
                )}
                {loaderData?.uuid &&
                  !!loaderData?.total &&
                  !isLoadingResults && (
                    <Grid
                      item
                      xs={true}
                      className="right-column-table"
                      sx={{
                        transition: 'all 0.2s ease-in-out',
                      }}>
                      <SearchResultsTable
                        page={pageNumber}
                        setPage={handlePageChange}
                        sx={{ marginLeft: '1px' }}
                        sites={loaderData.sites || []}
                        total={loaderData.total || 0}
                        sort={sortField}
                        order={sortOrder}
                        showSnippets={showSnippets === '1'}
                        highlighting={loaderData.highlighting}
                        onSortChange={(sort) => {
                          router.push({
                            path: route.path,
                            query: {
                              ...route.query,
                              sort:
                                sort === defaultSortString ? undefined : sort,
                            },
                          });
                        }}
                      />
                    </Grid>
                  )}
                {loaderData?.total === 0 && !isLoadingResults && (
                  <NoResultsFound />
                )}
                {isLoadingResults && <LoadingResults />}
                {!!loaderData?.error && (
                  <Grid item xs={12} className="right-column-error">
                    <Alert severity="error">
                      {loaderData?.error?.message ||
                        'Something went wrong. Please try again.'}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </>
        }

        {isEditingSearch && !lgAndUp && !isNewReportPage && (
          <Drawer
            open={isEditingSearch}
            variant="temporary"
            onClose={() => setIsEditingSearch(false)}
            anchor="bottom"
            PaperProps={{
              sx: { height: 'calc(100vh - 170px)' },
            }}>
            {RenderEditor}
          </Drawer>
        )}

        {!!loaderData?.uuid && loaderData?.search && (
          <DownloadDrawer
            open={downloadDrawerOpen}
            filters={filters}
            onClose={() => setDownloadDrawerOpen(false)}
            search={loaderData?.search}
            name={name}
            rowLimit={rowLimit}
            startDownload={(rows, columns) => {
              setDownloadRows(rows);
              setDownloadColumns(columns);
              setIsDownloading(true);
            }}
          />
        )}
        <DownloadLoadingModal
          open={
            isDownloading &&
            !download.file &&
            ((requiresDownloadRecaptcha && recaptchaToken && !recaptchaOpen) ||
              !requiresDownloadRecaptcha)
          }
        />
        <DownloadSuccess
          open={!!download.file && isDownloading}
          file={download.file}
          onClose={() => setIsDownloading(false)}
        />
        <RecaptchaDialog
          open={requiresDownloadRecaptcha && recaptchaOpen}
          onClose={(token) => {
            setRecaptchaOpen(false);
            if (token) {
              setRecaptchaToken(token);
            } else {
              setIsDownloading(false);
            }
          }}
          onError={(error) => {
            handleException(error, { category: 'download recaptcha' });
            setIsDownloading(false);
            setRecaptchaOpen(false);
          }}
        />
        <ForcedSignupDialog
          open={forcedSignupDialogOpen}
          onClose={() => setForcedSignupDialogOpen(false)}
          reason={
            Object.keys(filters).length > 0
              ? 'Filtering reports requires a free login'
              : 'Free Login Required'
          }
        />
        {!!loaderData?.retryAfter && (
          <SearchLoadingDialog
            open={!!loaderData?.retryAfter}
            retryAfter={+loaderData?.retryAfter}
            onCancel={() => {
              router.push({
                path: '/reports/new',
                query: {
                  ...route.query,
                  retryAfter: undefined,
                  search: undefined,
                },
              });
            }}
            totalRetries={5}
          />
        )}
      </Grid>
    </PageTemplate>
  );
}
